import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import {
  FieldMappingCreationAndEditDict,
  FieldMappingOptions,
} from "../../../../../../models/Entities";
import { createFieldMappingOptions } from "../../../../configuration/field-mappings/utils/FieldMappingUtils";
import { deleteLinkedAccountFieldMapping } from "../../../../../../api-client/APIClient";
import {
  Typeahead,
  Button,
  ButtonVariant,
  Card,
  Text,
  TextFieldVariant,
} from "@merge-api/merge-javascript-shared";
import { LinkedAccount } from "../../../../../../models/Entities";
import { showSuccessToast } from "../../../../../shared/Toasts";
import { navigateToFieldMappingSettings } from "../../../../../../router/RouterUtils";
import useUnmappedTargetCard from "./useUnmappedTargetCard";
import FieldMappingDropdownChild from "../../../../../shared/FieldMappingDropdownChild";
import FieldMappingsGridContainer from "../components/FieldMappingsGridContainer";
import EditJmesPathModal from "../modal/steps/advanced-field-mapping/components/EditJsonAdvancedMappingExpressionModal";
import { useRemoteFieldOptionFilter } from "../utils/remoteFieldSearch";
type FieldMappingUnmappedTargetCardProps = {
  linkedAccount: LinkedAccount;
  fieldKey: string;
  fieldDescription: string;
  fieldMappingOptions: FieldMappingOptions;
  commonModel: string;
  fieldMappingTargetId: string;
  isJmesPathDeprecationEnabled: boolean;
};
const FieldMappingUnmappedTargetCard = ({
  linkedAccount,
  fieldKey,
  fieldDescription,
  fieldMappingOptions,
  commonModel,
  fieldMappingTargetId,
  isJmesPathDeprecationEnabled,
}: FieldMappingUnmappedTargetCardProps) => {
  const history = useHistory();
  const createdfieldMappingOptions = useMemo(
    () => createFieldMappingOptions(fieldMappingOptions, commonModel),
    [fieldMappingOptions, commonModel],
  );
  const filterRemoteFields = useRemoteFieldOptionFilter(createdfieldMappingOptions);
  const {
    createdFieldMappingInstanceId,
    originFieldForAdvancedMapping,
    showAdvancedMappingButton,
    showAdvancedFieldMappingModal,
    setCreatedFieldMappingInstanceId,
    setOriginFieldForAdvancedMapping,
    setShowAdvancedMappingButton,
    setShowAdvancedFieldMappingModal,
    saveLinkedAccountMapping,
    editCreatedFieldMapping,
    jsonAdvancedMappingExpression,
    setJsonAdvancedMappingExpression,
  } = useUnmappedTargetCard();
  const [pendingSimpleField, setPendingSimpleField] = React.useState<any>(null);

  return (
    <>
      {showAdvancedFieldMappingModal && originFieldForAdvancedMapping && (
        <EditJmesPathModal
          currentJsonAdvancedMappingExpression={jsonAdvancedMappingExpression}
          jsonData={originFieldForAdvancedMapping.value}
          remoteFieldDisplayName={originFieldForAdvancedMapping.display_name}
          organizationTargetField={fieldKey}
          onModalClose={() => {
            setShowAdvancedFieldMappingModal(false);
          }}
          onUpdate={(jsonAdvancedMappingExpression: string) => {
            if (createdFieldMappingInstanceId) {
              editCreatedFieldMapping(
                originFieldForAdvancedMapping,
                jsonAdvancedMappingExpression,
                isJmesPathDeprecationEnabled,
              );
            } else {
              saveLinkedAccountMapping(
                originFieldForAdvancedMapping,
                linkedAccount,
                fieldKey,
                fieldDescription,
                commonModel,
                jsonAdvancedMappingExpression,
                isJmesPathDeprecationEnabled,
              );
            }
            setJsonAdvancedMappingExpression(jsonAdvancedMappingExpression);
            setShowAdvancedMappingButton(false);
            setShowAdvancedFieldMappingModal(false);
            setPendingSimpleField(null);
          }}
          linkedAccount={linkedAccount}
        />
      )}
      <FieldMappingsGridContainer>
        <>
          <div className="flex flex-col">
            <Typeahead
              className="w-100"
              borderVariant={TextFieldVariant.Shadowed}
              options={createdfieldMappingOptions}
              placeholder={
                createdfieldMappingOptions.length != 0
                  ? "Search Remote Fields by name or value..."
                  : "No Remote Fields currently available"
              }
              disabled={createdfieldMappingOptions.length === 0}
              multiple={false}
              getOptionLabel={(option: any) => {
                return option?.display_name ?? "";
              }}
              filterOptions={(_options, { inputValue }) => filterRemoteFields(inputValue)}
              blurOnSelect
              renderOption={(fieldMapping: FieldMappingCreationAndEditDict) => {
                return <FieldMappingDropdownChild option={fieldMapping} />;
              }}
              onChange={(_, selectedRemoteField: any) => {
                const showAdvancedFieldMappingsForm =
                  selectedRemoteField &&
                  ((selectedRemoteField.type.includes("list") &&
                    selectedRemoteField.value?.length !== 0) ||
                    selectedRemoteField.type == "dict");
                setJsonAdvancedMappingExpression(null);
                if (showAdvancedFieldMappingsForm) {
                  setOriginFieldForAdvancedMapping(selectedRemoteField);
                  setShowAdvancedMappingButton(true);
                  setPendingSimpleField(null);
                  return;
                }
                if (selectedRemoteField) {
                  if (isJmesPathDeprecationEnabled) {
                    setOriginFieldForAdvancedMapping(selectedRemoteField);
                    setPendingSimpleField(selectedRemoteField);
                    setShowAdvancedMappingButton(false);
                  } else {
                    if (createdFieldMappingInstanceId == null) {
                      saveLinkedAccountMapping(
                        selectedRemoteField,
                        linkedAccount,
                        fieldKey,
                        fieldDescription,
                        commonModel,
                        null,
                      );
                    } else {
                      editCreatedFieldMapping(selectedRemoteField, null);
                    }
                  }
                } else {
                  setPendingSimpleField(null);
                  if (createdFieldMappingInstanceId) {
                    deleteLinkedAccountFieldMapping(createdFieldMappingInstanceId, () => {
                      showSuccessToast("Successfully deleted Field Mapping");
                      setCreatedFieldMappingInstanceId(null);
                    });
                  }
                  setShowAdvancedMappingButton(false);
                }
              }}
            />
            {pendingSimpleField && (
              <div className="flex gap-2 mt-3 ml-auto">
                <Button
                  size="sm"
                  variant={ButtonVariant.SecondaryGray}
                  onClick={() => {
                    setShowAdvancedFieldMappingModal(true);
                  }}
                  rightIcon={<ArrowUpRight size={12} />}
                >
                  Advanced mapping
                </Button>
                <Button
                  size="sm"
                  variant={ButtonVariant.PrimaryGray}
                  onClick={() => {
                    if (createdFieldMappingInstanceId == null) {
                      saveLinkedAccountMapping(
                        pendingSimpleField,
                        linkedAccount,
                        fieldKey,
                        fieldDescription,
                        commonModel,
                        null,
                        isJmesPathDeprecationEnabled,
                      );
                    } else {
                      editCreatedFieldMapping(
                        pendingSimpleField,
                        null,
                        isJmesPathDeprecationEnabled,
                      );
                    }
                    setPendingSimpleField(null);
                  }}
                >
                  Save
                </Button>
              </div>
            )}
            {showAdvancedMappingButton && (
              <Button
                size="sm"
                variant={ButtonVariant.SecondaryGray}
                onClick={() => {
                  setShowAdvancedFieldMappingModal(true);
                }}
                className="mt-3 ml-auto"
                rightIcon={<ArrowUpRight size={12} />}
              >
                Advanced mapping
              </Button>
            )}
            {jsonAdvancedMappingExpression && (
              <div className="flex items-center px-[10px] justify-between bg-blue-0 rounded-md text-sm mt-3 h-[30px]">
                <div className="truncate">{jsonAdvancedMappingExpression}</div>
                <Button
                  size="sm"
                  variant={ButtonVariant.TextBlue}
                  rightIcon={<ArrowUpRight size={12} />}
                  onClick={() => {
                    setShowAdvancedFieldMappingModal(true);
                  }}
                >
                  Advanced
                </Button>
              </div>
            )}
          </div>
          <div className="flex justify-center pt-3">
            <ArrowRight size="20" />
          </div>
          <Card size="lg" variant="outline" className="py-4 px-5 flex flex-col">
            <div className="flex flex-grow-1 justify-between mb-2">
              <Text variant="lg-mono">{fieldKey}</Text>
              <Button
                variant={ButtonVariant.TextBlue}
                size="sm"
                onClick={() => navigateToFieldMappingSettings(history, fieldMappingTargetId)}
              >
                View
              </Button>
            </div>
            {fieldDescription && (
              <Text variant="sm" className="text-gray-40">
                {fieldDescription}
              </Text>
            )}
          </Card>
        </>
      </FieldMappingsGridContainer>
    </>
  );
};
export default FieldMappingUnmappedTargetCard;
